const {getIn, setIn, extendIn} = require("../../../../shared/ImmHelpers");

/**
 * A tiny wrapper for a large state object and a path into it. Makes it easy
 * to immutably update or access the entire state at some lower level while
 * still holding a reference to everything above.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const Cursor = function(state, path, options) {
	if (!(this instanceof Cursor)) {
		return new Cursor(state, path, options);
	}
	this.state = state;
	this.path = path || [];
	this.options = options || {};
	this._bad = false;
};

const withState = function(cr, state) {
	return new Cursor(state, cr.path, cr.options);
};

const withPath = function(cr, path) {
	return new Cursor(
		cr.state,
		path,
		setIn(cr.options, "defaultValue", undefined)
	);
};

Cursor.prototype.at = function(keyList) {
	if (!Array.isArray(keyList)) {
		keyList = String(keyList).split(".");
	}
	return withPath(this, keyList || []);
};

Cursor.prototype.root = function() {
	return withPath(this, []);
};

Cursor.prototype.in = function(keyList) {
	if (this._bad) {
		return this;
	}
	// Handle a weird case where keyList is falsy.
	if (keyList === 0) {
		keyList = ["0"];
	} else if (keyList && !Array.isArray(keyList)) {
		keyList = String(keyList).split(".");
	}
	return keyList ? withPath(this, this.path.concat(keyList)) : this;
};

// Returns a cloned cursor, representing one that references a nonexistent
// object. It makes in calls a noop, returns undefined when using get, and
// throws when using set/extend/etc. Going back to the root with methods like
// at will return it to normal.
Cursor.prototype.bad = function() {
	const cr = new Cursor(this.state);
	cr._bad = true;
	return cr;
};

// Sets a default value on the cursor. The cursor default value resets to
// undefined whenever the path changes. The default value affects the following
// cursor methods:
// - get: returns the default value if it would otherwise return undefined.
// - set: if an old -> new function is passed to set, then that function will
//   be passed the default value in place of undefined.
Cursor.prototype.withDefault = function(value) {
	return new Cursor(
		this.state,
		this.path,
		setIn(this.options, "defaultValue", value)
	);
};

// Returns a cursor which is allowed to create intermediate objects if set
// is called when the path doesn't exist, as opposed to throwing. Call this
// method with argument false to unset the option.
Cursor.prototype.canCreate = function(newVal = true) {
	return new Cursor(
		this.state,
		this.path,
		setIn(this.options, "canCreate", !!newVal)
	);
};

// Returns whether the path into the state exists.
Cursor.prototype.exists = function() {
	return this.get() !== undefined;
};

Cursor.prototype.getRoot = function() {
	return this.state;
};

Cursor.prototype.getPath = function() {
	return this.path;
};

Cursor.prototype.get = function(defaultValue) {
	defaultValue =
		defaultValue === undefined ? this.options.defaultValue : defaultValue;
	return this._bad ? defaultValue : getIn(this.state, this.path, defaultValue);
};

Cursor.prototype.getIn = function(keyList, defaultValue) {
	return this.in(keyList).get(defaultValue);
};

// Helper that produces the verbIn methods below.
const makeIn = function(methodName) {
	return function(keyList, ...args) {
		return this.in(keyList)[methodName](...args);
	};
};

const existsOneUp = function(cr) {
	return cr.at(cr.path.slice(0, cr.path.length - 1)).exists();
};

// Helper for determining when to throw when using set and friends.
const canSet = function(cr) {
	return existsOneUp(cr) || (!cr._bad && cr.options.canCreate);
};

Cursor.prototype.set = function(newValue) {
	if (!canSet(this)) {
		const path = this._bad ? "<bad>" : this.path.join(".");
		throw new Error("Called set on cursor with bad path " + path);
	}
	let realValue = newValue;
	if (
		typeof newValue === "function" &&
		this.options.defaultValue !== undefined
	) {
		realValue = x => newValue(x === undefined ? this.options.defaultValue : x);
	}
	return withState(this, setIn(this.state, this.path, realValue)).getRoot();
};
Cursor.prototype.setIn = makeIn("set");

Cursor.prototype.extend = function(valuesToSet) {
	if (!canSet(this)) {
		const path = this._bad ? "<bad>" : this.path.join(".");
		throw new Error("Called extend on cursor with bad path " + path);
	}
	return withState(
		this,
		extendIn(this.state, this.path, valuesToSet)
	).getRoot();
};
Cursor.prototype.extendIn = makeIn("extend");

// Updates multiple keys of one object with minimal cloning. The function f
// takes the old value and the key and returns the new value.
Cursor.prototype.setKeys = function(keysToSet, f) {
	if (!canSet(this)) {
		const path = this._bad ? "<bad>" : this.path.join(".");
		throw new Error("Called setKeys on cursor with bad path " + path);
	}
	return this.set(oldValues => {
		if (!oldValues) {
			oldValues = {};
		}
		const newValues = {};
		keysToSet.forEach(k => {
			newValues[k] = f(oldValues[k], k);
		});
		return extendIn(oldValues, [], newValues);
	});
};
Cursor.prototype.setKeysIn = makeIn("setKeys");

module.exports = Cursor;
